import React from 'react';
import { Box, Typography, Input } from '@lucid/lego';
import type { Theme } from '../types';

type SearchInputProps = {
  /** Theme for the component */
  theme: Theme;
  /** Label text above the search input */
  label: string;
  /** Placeholder text for the search input */
  placeholder: string;
  /** Current search value */
  value: string;
  /** Callback when search value changes */
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Callback when key is pressed */
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Test ID for testing */
  dataTestId: string;
};

/**
 * Search input component for VIN search
 * @component
 * @param {SearchInputProps} props - Component props
 * @returns {JSX.Element} Search input with label
 * @example
 * <SearchInput
 *   theme="dark"
 *   label="Locate Asset"
 *   placeholder="Search VIN..."
 *   value={query}
 *   onChange={handleChange}
 *   onKeyDown={handleKeyDown}
 *   dataTestId="vin-search"
 * />
 */
const SearchInput: React.FC<SearchInputProps> = ({
  theme,
  label,
  placeholder,
  value,
  onChange,
  onKeyDown,
  dataTestId,
}) => {
  return (
    <Box>
      <Typography variant="body" size="large" color="gray-1" theme={theme}>
        {label}
      </Typography>
      <Box mt={8}>
        <Input
          name="vin-search"
          type="text"
          placeholder={placeholder}
          theme={theme}
          size="medium"
          controlled={true}
          helperIcon={{
            category: 'global',
            name: 'search',
          }}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          data-testid={`${dataTestId}-input`}
        />
      </Box>
    </Box>
  );
};

export default SearchInput;
